<script lang="ts">
  import { onMount } from "svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import PopoverList from "@shared/svelte/Components/Popover/PopoverList.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import PaymentMethodUpdateModal from "@retailer/svelte/Payments/Settings/PaymentMethodUpdateModal.svelte";
  import Flash from "@shared/services/flashv2.js";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";

  import {
    paymentInstrumentsStore,
    pendingPaymentInstruments,
  } from "@retailer/svelte/stores/PaymentInstrumentStore";

  import * as PaymentInstrumentService from "@retailer/services/payment_instrument_service";

  import type { PaymentInstrument } from "@shared/types/PaymentInstrument";
  import type { Errors } from "@shared/types/Errors";
  import AddPaymentInstrument from "@retailer/svelte/Payments/AddPaymentInstrument.svelte";
  import { PlaidCustomization } from "@retailer/types/Plaid";
  import * as BankAccountService from "@retailer/services/bank_account_service";
  import { updatingBankAccount } from "@retailer/stores/BankAccountStore";

  export let showModals: {};
  export let onCloseModals: () => void;
  export let afterInstrumentAddSuccess: () => void;
  export let afterSuccess: () => void;
  export let afterMicrodepositFailure: () => void;

  // data
  let plaidModals;
  let plaidModalsBuilt = false;

  let instrumentToRemove: PaymentInstrument;
  let errors: Errors;
  const maxNumberOfPaymentMethods = 10;

  // functions
  function onMakeDefault(instrument: PaymentInstrument) {
    const errCB = res => {
      errors = res;
    };

    PaymentInstrumentService.setDefaultInstrument(instrument, errCB);
    onCloseModals();
  }

  function iconType(instrument: PaymentInstrument) {
    if (instrument.instrument_type != "credit_card") {
      return "ACH";
    }
    if (["VISA", "AMEX", "DISCOVER", "MASTERCARD"].indexOf(instrument.brand) > -1) {
      return instrument.brand;
    }
    return "CreditCard";
  }

  const onDelete = (instrument: PaymentInstrument) => {
    deletePaymentInstrument(instrument.id);
    instrumentToRemove = null;
    onCloseModals();
  };

  const deletePaymentInstrument = (id: number) => {
    // callbacks
    const showNotFoundMessage = (res: Response) => {
      if (res.status != 404) return;

      Flash.now({
        type: "success",
        header: "Payment Method Deleted",
        message: "This payment method was already deleted in another active session.",
      });
    };
    const showSuccessMessage = () => {
      Flash.now({
        type: "success",
        message: "Your payment method was deleted.",
      });
    };

    PaymentInstrumentService.deletePaymentInstrument(id, showSuccessMessage, showNotFoundMessage);
  };

  const onConfirmDelete = (instrument: PaymentInstrument) => {
    instrumentToRemove = instrument;
    showModals["PaymentMethodUpdateModal"] = true;
  };

  async function configureModal() {
    BankAccountService.buildPlaidModal({
      isMicrodeposit: true,
      customization: PlaidCustomization.MultiSelect,
      afterSuccess: afterSuccess,
      afterMicrodepositFailure: afterMicrodepositFailure,
      plaidAccountIds: accountIdsForPlaidModals,
    }).then(res => {
      if (res) {
        plaidModalsBuilt = true;
        plaidModals = res;
      }
    });
  }

  onMount(async () => {
    if (pendingInstrumentsExist) {
      configureModal();
    }
  });

  $: accountLimitReached =
    $paymentInstrumentsStore.paymentInstruments.length + $pendingPaymentInstruments.length >= 10;
  $: pendingInstrumentsExist = $pendingPaymentInstruments.length > 0;
  $: spin = $updatingBankAccount || !plaidModalsBuilt;
  $: accountIdsForPlaidModals = $pendingPaymentInstruments
    .filter(
      instrument => instrument.plaid_account.verification_status == "pending_manual_verification"
    )
    .map(instrument => instrument.plaid_account.id);

</script>

<FormErrors {errors} />
<section class="fizz-stack-32 payment-instruments">
  <h2 class="fizz-heading-2 fizz-stack-8">Payment Methods</h2>
  <p class="fizz-text-large fizz-stack-16">
    Up to {maxNumberOfPaymentMethods} payment methods can be saved, in order to pay invoices from participating
    distributors.
  </p>
  {#if pendingInstrumentsExist}
    <div
      class="fizz-text-medium fizz-surface-blue fizz-inset-square-24 fizz-space-between fizz-radius-xlarge fizz-align-content-start fizz-bordered"
    >
      <Icon type="Info" />
      <div>
        <h4 class="fizz-heading-4">You have an account pending verification</h4>
        <p>
          In order to verify your bank account, we will be making one $0.01 deposit in your account
          from “#XXX Provi ACCTVERIFY” within 1-2 business days. When you receive the deposit, enter
          below the first 3 letters in the test deposit transaction name (e.g #ABC).
        </p>
      </div>
    </div>
  {/if}
  {#each $paymentInstrumentsStore.paymentInstruments as instrument}
    <div class="payment-instruments-row fizz-border-bottom" id="instrument-{instrument.id}">
      <div
        class="payment-instrument-row-content fizz-flex-row fizz-space-between fizz-inset-vertical-16"
      >
        <div class="fizz-flex-row">
          <Icon type={iconType(instrument)} height="24" width="34" />
          <p>
            {instrument.nickname}
            {#if instrument.default_instrument}(Default){/if}
          </p>
        </div>
        <PopoverList
          item={instrument}
          extraClasses="payments-methods"
          options={[
            {
              name: "Make Default",
              action: () => onMakeDefault(instrument),
              hide: instrument.default_instrument,
            },
            {
              name: "Delete",
              action: () => onConfirmDelete(instrument),
            },
          ]}
          openDirection={"bottom-left-payments-methods"}
          openMenu={showModals["ShowInstrumentMenu"]}
          icon={{ type: "MoreVert", height: "20", width: "20" }}
          useButtonElement={false}
        />
      </div>
    </div>
  {/each}
  {#each $pendingPaymentInstruments as instrument}
    <div
      class="payment-instruments-row fizz-border-bottom"
      id="instrument-{instrument.plaid_account.id}"
    >
      <div
        class="payment-instrument-row-content fizz-flex-row fizz-space-between fizz-inset-vertical-16"
      >
        <div class="fizz-flex-row">
          <Icon type="ACH" height="24" width="34" />
          <td data-label="Status">
            <span class="fizz-pill-yellow"> Pending </span>
          </td>
          <p>
            {#if instrument.institution_name}
              {instrument.institution_name} - {instrument.plaid_account.mask}
            {:else}
              {instrument.plaid_account.name}
            {/if}
          </p>
          {#if instrument.plaid_account.verification_status == "pending_manual_verification"}
            <button
              class="fizz-button-primary"
              class:fizz-button-spinner={spin}
              data-testid="button-{instrument.plaid_account.id}"
              on:click|preventDefault={() => {
                plaidModals[instrument.plaid_account.account_id].open();
              }}
              disabled={spin}
            >
              Enter Amounts
            </button>
          {/if}
        </div>
      </div>
    </div>
  {/each}
</section>
<Authorization authName={"payment_instruments.create"}>
  <AddPaymentInstrument
    customization={PlaidCustomization.MultiSelect}
    afterSuccess={afterInstrumentAddSuccess}
    disabled={accountLimitReached}
  />
</Authorization>

<PaymentMethodUpdateModal
  bind:showModals
  paymentInstruments={$paymentInstrumentsStore.paymentInstruments.filter(
    instrument => !instrument.default_instrument
  )}
  {instrumentToRemove}
  {iconType}
  {onCloseModals}
  {onMakeDefault}
  {onDelete}
/>
